import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'

import FeatureEditImage from '@/images/app-banner/feature-edit.svg'
import FeaturePhoneImage from '@/images/app-banner/feature-phone.svg'
import FeatureSpeedImage from '@/images/app-banner/feature-speed.svg'
import FeatureDealsImage from '@/images/app-banner/feature-deals.svg'
import QrImageCs from '@/images/app-banner/mobile-app-links-qr-cs.webp'
import QrImageEn from '@/images/app-banner/mobile-app-links-qr-en.webp'
import QrImageDe from '@/images/app-banner/mobile-app-links-qr-de.webp'
import QrImagePl from '@/images/app-banner/mobile-app-links-qr-pl.webp'
import AndroidBadgeCs from '@/images/app-banner/android-cs.png'
import AndroidBadgeEn from '@/images/app-banner/android-en.png'
import AndroidBadgeDe from '@/images/app-banner/android-de.png'
import AndroidBadgePl from '@/images/app-banner/android-pl.png'
import IphoneBadgeCs from '@/images/app-banner/iphone-cs.svg'
import IphoneBadgeEn from '@/images/app-banner/iphone-en.svg'
import IphoneBadgeDe from '@/images/app-banner/iphone-de.svg'
import IphoneBadgePl from '@/images/app-banner/iphone-pl.svg'

const qrImages = {
    cs: QrImageCs,
    en: QrImageEn,
    de: QrImageDe,
    pl: QrImagePl,
} as const

const androidBadges = {
    cs: AndroidBadgeCs,
    en: AndroidBadgeEn,
    de: AndroidBadgeDe,
    pl: AndroidBadgePl,
} as const

const iphoneBadges = {
    cs: IphoneBadgeCs,
    en: IphoneBadgeEn,
    de: IphoneBadgeDe,
    pl: IphoneBadgePl,
} as const

const featureIcons = [
    FeatureEditImage,
    FeaturePhoneImage,
    FeatureSpeedImage,
    FeatureDealsImage,
] as const

function normalizeBannerLocale(locale: string): keyof typeof qrImages {
    const normalizedLocale = locale.toLowerCase().split(/[-_]/)[0]

    if (normalizedLocale in qrImages) {
        return normalizedLocale as keyof typeof qrImages
    }

    return 'cs'
}

export default function CheckoutSuccessAppBanner({
    locale,
}: {
    locale: string
}) {
    const { t } = useTranslation()
    const normalizedLocale = normalizeBannerLocale(locale)
    const features = [
        t('success.appBanner.feature1'),
        t('success.appBanner.feature2'),
        t('success.appBanner.feature3'),
        t('success.appBanner.feature4'),
    ]

    return (
        <Box sx={{
            backgroundColor: '#FFC61A',
            borderRadius: '10px',
            overflow: 'hidden',
            px: {
                xs: 3,
                sm: 4,
            },
            pt: {
                xs: 3,
                sm: 4,
            },
            pb: {
                xs: 0,
                lg: 4,
            },
        }}>
            <Typography
                level="h3"
                sx={{
                    color: '#111827',
                    fontWeight: 600,
                    textAlign: 'center',
                    mb: 3,
                    fontSize: {
                        xs: '1.375rem',
                        md: '1.5rem',
                    },
                    lineHeight: 1.2,
                }}
            >
                {t('success.appBanner.title')}
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    lg: 'minmax(0, 1fr) minmax(220px, 260px)',
                },
                gap: 2.5,
                alignItems: 'center',
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}>
                        {features.map((feature, index) => (
                            <Box
                                key={feature}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={featureIcons[index]}
                                    alt="Icon"
                                    aria-hidden="true"
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: '#111827',
                                        fontSize: {
                                            xs: '0.95rem',
                                            sm: '1.05rem',
                                        },
                                        lineHeight: 1.35,
                                        fontWeight: 500,
                                    }}
                                >
                                    {feature}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        mt: 2.5,
                    }}>
                        <Box
                            component="a"
                            href={`https://play.google.com/store/apps/details?id=com.mrparkit.twa&hl=${normalizedLocale}`}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ display: 'inline-flex' }}
                        >
                            <Box
                                component="img"
                                src={androidBadges[normalizedLocale]}
                                alt={t('success.appBanner.androidAlt')}
                                sx={{
                                    height: 40,
                                    width: 'auto',
                                    display: 'block',
                                    maxWidth: '100%',
                                }}
                            />
                        </Box>
                        <Box
                            component="a"
                            href={`https://apps.apple.com/cz/app/mr-parkit/id6615079570?platform=iphone&l=${normalizedLocale}`}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ display: 'inline-flex' }}
                        >
                            <Box
                                component="img"
                                src={iphoneBadges[normalizedLocale]}
                                alt={t('success.appBanner.iphoneAlt')}
                                sx={{
                                    height: 40,
                                    width: 'auto',
                                    display: 'block',
                                    maxWidth: '100%',
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{
                    display: 'flex',
                    justifyContent: {
                        xs: 'center',
                        lg: 'flex-end',
                    },
                }}>
                    <Box
                        component="img"
                        src={qrImages[normalizedLocale]}
                        alt={t('success.appBanner.visualAlt')}
                        sx={{
                            width: '100%',
                            marginBottom: -10,
                            maxWidth: {
                                xs: 280,
                                sm: 300,
                                lg: 250,
                            },
                            height: 'auto',
                            display: 'block',
                        }}
                    />
                </Box>
            </Box>
        </Box>
    )
}
